/**
 * React Query hooks for tax form reconciliation (1099-DA, 1099-B, 1099-MISC, 1099-INT)
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { authorizedAxios } from "@moonscape/shared";

// Types
export type FormType = "1099-DA" | "1099-B" | "1099-MISC" | "1099-INT";
export type MatchStatus = "matched" | "discrepancy" | "unmatched" | "pending";

export interface TaxFormRecord {
  id: number;
  taxYear?: number;
  formType?: FormType;
  exchange: string | null;
  brokerAccountId?: string | null;
  assetSymbol: string | null;
  dtifCode?: string | null;
  quantity?: number | string | null;
  acquiredDate?: string | null;
  disposedDate: string | null;
  proceeds: number | null;
  costBasis?: number | null;
  // 1099-DA specific
  gainLossClassification?: "short-term" | "long-term" | null;
  isNoncoveredSecurity?: boolean | null;
  isAggregated: boolean;
  aggregatedTransactions?: number | null;
  // 1099-MISC specific
  incomeType?: string | null;
  // 1099-INT specific
  interestAmount?: number | null;
  // Reconciliation
  matchedTxnId: number | null;
  matchedRawTxnId: number | null;
  proceedsMoonscape?: number | null;
  costBasisMoonscape?: number | null;
  proceedsDelta: number | null;
  costBasisDelta?: number | null;
  matchReason?: "exchange_mismatch" | "partial_match" | "aggregated" | null;
  matchConfidence?: number | null;
  status: MatchStatus;
  reviewedAt: string | null;
  reviewedNotes: string | null;
  importBatchId?: string;
  createdAt?: string;
  // Joined from txns when matched
  txn?: {
    id: number;
    type: string;
    timestamp: number;
    sellAsset: string;
    sellQty: number;
  } | null;
}

export interface TaxFormImportBatch {
  batchId: string;
  formType: FormType;
  exchange: string | null;
  taxYear: number;
  recordCount: number;
  createdAt: string;
  reconciliationStatus?: "pending" | "completed";
  summary: {
    matched: number;
    discrepancy: number;
    unmatched: number;
    pending?: number;
    totalDelta?: number;
  };
}

export interface ReconciliationSummary {
  batchId: string;
  formType: FormType;
  exchange: string | null;
  taxYear: number;
  total: number;
  matched: number;
  discrepancy: number;
  unmatched: number;
  pending: number;
  totalDelta?: number;
  records: TaxFormRecord[];
}

export interface UnreportedTransaction {
  id: number;
  asset: string;
  date: string;
  type: string;
  proceeds: number;
  exchange: string;
  timestamp: number;
}

export interface ImportTaxFormParams {
  formType: FormType;
  taxYear: number;
  exchange: string;
  file?: File;
  csvContent?: string;
}

// Query keys
const QUERY_KEYS = {
  imports: (taxYear?: number) => ["tax-form-imports", taxYear] as const,
  results: (batchId: string) => ["tax-form-results", batchId] as const,
};

/**
 * Fetch all tax form import batches for a user
 */
export function useTaxFormImports(taxYear?: number) {
  const { user, getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: QUERY_KEYS.imports(taxYear),
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const params = taxYear ? { taxYear } : {};
      const response = await authorizedAxios(token).get("/tax-forms/imports", {
        params,
      });
      return response.data?.data as TaxFormImportBatch[];
    },
    enabled: !!user?.sub,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Import a tax form CSV
 */
export function useImportTaxForm() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      formType,
      taxYear,
      exchange,
      file,
      csvContent,
    }: ImportTaxFormParams) => {
      const token = await getAccessTokenSilently();
      const formData = new FormData();
      formData.append("formType", formType);
      formData.append("taxYear", String(taxYear));
      formData.append("exchange", exchange);
      if (file) {
        formData.append("file", file);
      } else if (csvContent) {
        formData.append("csvContent", csvContent);
      }

      const response = await authorizedAxios(token).post(
        "/tax-forms/import",
        formData
      );
      return response.data as {
        batchId: string;
        recordCount: number;
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.imports(variables.taxYear),
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.imports() });
    },
  });
}

/**
 * Get reconciliation results for a batch
 */
export function useTaxFormReconciliation(batchId: string | null) {
  const { user, getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: QUERY_KEYS.results(batchId || ""),
    queryFn: async () => {
      if (!batchId) return null;
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).get(
        `/tax-forms/${batchId}/results`
      );
      const data = response.data;
      return {
        batchId: data.batchId,
        formType: data.formType,
        exchange: data.exchange,
        taxYear: data.taxYear,
        total: data.summary?.total ?? 0,
        matched: data.summary?.matched ?? 0,
        discrepancy: data.summary?.discrepancy ?? 0,
        unmatched: data.summary?.unmatched ?? 0,
        pending: data.summary?.pending ?? 0,
        records: data.records || [],
      } as ReconciliationSummary;
    },
    enabled: !!user?.sub && !!batchId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Run reconciliation on an imported batch
 */
export function useRunReconciliation() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (batchId: string) => {
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).post(
        `/tax-forms/${batchId}/reconcile`
      );
      return response.data as { batchId: string };
    },
    onSuccess: (data, batchId) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.results(data?.batchId || batchId),
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.imports() });
    },
  });
}

/**
 * Review a tax form record (mark as reviewed with notes)
 */
export function useReviewTaxFormRecord() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      batchId,
      recordId,
      notes,
    }: {
      batchId: string;
      recordId: number;
      notes?: string;
    }) => {
      const token = await getAccessTokenSilently();
      await authorizedAxios(token).patch(
        `/tax-forms/${batchId}/records/${recordId}/review`,
        { notes }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.results(variables.batchId),
      });
    },
  });
}

/**
 * Delete an import batch
 */
export function useDeleteTaxFormBatch() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (batchId: string) => {
      const token = await getAccessTokenSilently();
      await authorizedAxios(token).delete(`/tax-forms/${batchId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.imports() });
    },
  });
}

/**
 * Fetch unreported transactions — taxable events in Moonscape not found in any 1099-DA
 */
export function useUnreportedTransactions(
  taxYear: number,
  exchange?: string
) {
  const { user, getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ["form1099da-unreported", taxYear, exchange || ""],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams();
      params.append("taxYear", taxYear.toString());
      if (exchange) params.append("exchange", exchange);
      const res = await authorizedAxios(token).get(
        `/api/form-1099da/unreported?${params.toString()}`
      );
      const data = res.data;
      return (Array.isArray(data) ? data : []) as UnreportedTransaction[];
    },
    enabled: !!user?.sub && taxYear >= 2025,
    staleTime: 1000 * 60 * 5,
  });
}
